import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { FiArrowRight, FiX } from 'react-icons/fi';
import { Avatar, CoverImage } from '../ui/Avatar';
import musicadeBcg from '../../assets/musicade-bcg.png';
import { AuthGateModal } from '../common/AuthGateModal';
import type { AssociatedCreator, EventCreatorProfile, EventHost } from '../../api/events';
import { useFollowUser, useUnfollowUser, useGetPublicProfile } from '../../api/profile';
import { getProfileIdentifier } from '../../utils/profileUrl';
import { formatTagLabel } from '../../utils/tagLabel';

const MAX_VISIBLE_CREATORS = 3;

function formatCreatorRole(role: string): string {
  return formatTagLabel(role.replace(/_/g, ' '));
}

function ExpandIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M12.6667 7.99967V5.99965C12.6667 4.74257 12.6666 4.11404 12.2761 3.72352C11.8856 3.333 11.2571 3.333 10 3.33301L8 3.33301"
        stroke="white"
        strokeWidth="1.5"
        strokeMiterlimit="16"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.33398 8L3.334 10C3.33401 11.2571 3.33402 11.8856 3.72454 12.2761C4.11507 12.6667 4.7436 12.6667 6.00068 12.6667H8.00065"
        stroke="white"
        strokeWidth="1.5"
        strokeMiterlimit="16"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

interface HostDetailsModalProps {
  creator: EventCreatorProfile;
  identifier: string;
  isSelf: boolean;
  isFollowing: boolean;
  followBusy: boolean;
  bio?: string;
  eventsCount?: number;
  associatedCreators: AssociatedCreator[];
  onFollowToggle: () => void;
  onClose: () => void;
}

function HostDetailsModal({
  creator,
  identifier,
  isSelf,
  isFollowing,
  followBusy,
  bio,
  eventsCount,
  associatedCreators,
  onFollowToggle,
  onClose,
}: HostDetailsModalProps) {
  const displayName = creator.full_name || creator.username;

  return (
    <div
      className="fixed inset-0 z-70 flex items-end sm:items-center justify-center px-3 py-3 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" onClick={onClose} />

      <div
        className="relative w-full max-w-md h-152 max-h-[calc(100dvh-1.5rem)] overflow-hidden rounded-2xl border border-[#1E1E28] shadow-[0_16px_32px_0_rgba(0,0,0,0.40)] flex flex-col"
        style={{
          background:
            'linear-gradient(90deg, rgba(255, 255, 255, 0.10) 42.32%, rgba(255, 255, 255, 0.00) 156.33%), #000',
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="relative h-39 shrink-0">
          <CoverImage src={musicadeBcg} className="h-full">
            <div className="absolute inset-0 bg-linear-to-b from-black/20 via-black/35 to-black/88" />
            <div className="absolute inset-0 flex h-full w-full flex-col justify-between p-4 sm:p-5">
              <div className="flex items-start justify-between gap-4 self-stretch">
                <Avatar
                  src={creator.prof_pics ?? creator.profile_image}
                  alt={creator.username}
                  name={displayName}
                  size="lg"
                  ring
                />

                <button
                  type="button"
                  onClick={onClose}
                  className="w-10 h-10 rounded-full bg-black/55 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white/90 hover:bg-black/75 transition-colors shrink-0"
                  aria-label="Close host details"
                >
                  <FiX size={18} />
                </button>
              </div>

              <div className="flex items-end justify-between gap-4 self-stretch">
                <Link
                  to="/profile/$identifier"
                  params={{ identifier }}
                  className="min-w-0 group"
                  onClick={(event) => event.stopPropagation()}
                >
                  <p className="truncate text-[#FFF] text-[20px] font-bold leading-none group-hover:text-[color-mix(in_oklab,var(--event-accent)_75%,white)] transition-colors">
                    {displayName}
                  </p>
                  <p className="mt-1 truncate text-[#A1A1AA] text-[12px] font-medium leading-none">
                    @{creator.username}
                  </p>
                </Link>

                {!isSelf && (
                  <button
                    type="button"
                    onClick={onFollowToggle}
                    disabled={followBusy}
                    className={`min-w-32 sm:min-w-36 h-12 px-6 rounded-xl flex items-center justify-center text-[14px] font-semibold transition-colors disabled:opacity-50 shadow-[0_4px_8px_0_rgba(255,255,255,0.08)] ${
                      isFollowing
                        ? 'bg-zinc-800 border border-white/10 text-white hover:bg-zinc-700'
                        : 'bg-white text-black hover:bg-zinc-200'
                    }`}
                  >
                    {followBusy ? 'Processing...' : isFollowing ? 'Following' : 'Follow'}
                  </button>
                )}
              </div>
            </div>
          </CoverImage>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain no-scrollbar px-5 sm:px-6 py-3 sm:py-4 space-y-5">
          <div>
            {typeof eventsCount === 'number' && eventsCount > 0 && (
              <>
                <p className="text-white text-[40px] sm:text-[44px] font-bold leading-none">
                  {eventsCount}
                </p>
                <p className="text-zinc-500 text-[12px] uppercase tracking-[0.28em] font-semibold mt-2">
                  Events Hosted
                </p>
              </>
            )}

            {bio && (
              <p className="text-zinc-300 text-[17px] leading-relaxed mt-4 max-w-160">{bio}</p>
            )}
          </div>

          {associatedCreators.length > 0 && (
            <div>
              <p className="text-zinc-500 text-[12px] uppercase tracking-[0.28em] font-semibold mb-3">
                Associated Creators
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {associatedCreators.map((creatorItem) => {
                  const displayNameItem =
                    creatorItem.full_name?.split(' ')[0] ?? creatorItem.username;

                  return (
                    <div
                      key={creatorItem.id}
                      className="flex items-center justify-between gap-3 min-w-0 rounded-full border border-white/10 bg-zinc-950/90 px-3 py-2.5"
                    >
                      <div className="flex items-center gap-2.5 min-w-0 flex-1">
                        <Avatar
                          src={creatorItem.profile_image ?? undefined}
                          alt={creatorItem.username}
                          name={displayNameItem || creatorItem.username}
                          size="xs"
                        />
                        <Link
                          to="/profile/$identifier"
                          params={{
                            identifier: getProfileIdentifier({
                              id: creatorItem.id,
                              username: creatorItem.username,
                            }),
                          }}
                          className="min-w-0 flex-1 text-zinc-200 text-[14px] font-medium truncate hover:text-white transition-colors"
                          title={displayNameItem}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {displayNameItem}
                        </Link>
                      </div>
                      {creatorItem.role && (
                        <span
                          className="max-w-24 px-2.5 py-1 rounded-full bg-blue-500/15 border border-blue-500/25 text-blue-400 text-[11px] font-medium shrink-0 truncate"
                          title={formatCreatorRole(creatorItem.role)}
                        >
                          {formatCreatorRole(creatorItem.role)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface EventOrganizerCardProps {
  creator: EventCreatorProfile;
  profileId?: number | string;
  currentUserId?: string | number;
  isAuthenticated: boolean;
  associatedCreators?: AssociatedCreator[];
}

export function EventOrganizerCard({
  creator,
  profileId,
  currentUserId,
  isAuthenticated,
  associatedCreators = [],
}: EventOrganizerCardProps) {
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [isHostModalOpen, setIsHostModalOpen] = useState(false);

  const host = creator as EventHost;
  const followIdentifier = creator.username || creator.id;
  const { data: organizerProfile } = useGetPublicProfile(followIdentifier);
  const { mutate: follow, isPending: followPending } = useFollowUser();
  const { mutate: unfollow, isPending: unfollowPending } = useUnfollowUser();

  // Guard against incomplete creator data
  if (!creator?.username) return null;

  const identifier = getProfileIdentifier({
    id: profileId ?? creator.id,
    username: creator.username,
  });

  const isSelf = !!currentUserId && String(creator.id) === String(currentUserId);
  const isFollowing = host.is_following ?? !!organizerProfile?.is_following;
  const followBusy = followPending || unfollowPending;
  const coverPhoto = musicadeBcg;
  const bio = host.bio ?? organizerProfile?.bio;
  const eventsCount = host.events_hosted_count;

  const visibleCreators = associatedCreators.slice(0, MAX_VISIBLE_CREATORS);
  const overflowCount = associatedCreators.length - visibleCreators.length;

  function handleFollowToggle() {
    if (!isAuthenticated) {
      setShowAuthGate(true);
      return;
    }
    if (isFollowing) {
      unfollow({ userId: creator.id, username: creator.username });
    } else {
      follow({ userId: creator.id, username: creator.username });
    }
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={0}
        aria-label={`Open hosted by details for ${creator.full_name || creator.username}`}
        onClick={() => setIsHostModalOpen(true)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setIsHostModalOpen(true);
          }
        }}
        className="group rounded-3xl bg-zinc-950 border border-white/8 overflow-hidden flex flex-col sm:flex-row items-stretch cursor-pointer transition-all duration-200 hover:border-white/15 hover:shadow-[0_18px_50px_-24px_rgba(0,0,0,0.8)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60"
      >
        {/* Left: photo panel */}
        <div className="w-full sm:w-[38%] sm:min-w-32 shrink-0">
          <CoverImage src={coverPhoto} className="h-full min-h-55 sm:min-h-full">
            <div className="absolute inset-0 bg-linear-to-t from-black/90 via-black/35 to-black/10" />
            <div className="absolute inset-0 flex flex-col p-3 sm:p-4">
              <div className="flex flex-col gap-3">
                <Avatar
                  src={creator.prof_pics ?? creator.profile_image}
                  alt={creator.username}
                  name={creator.full_name || creator.username}
                  size="md"
                  ring
                />
                <div className="min-w-0">
                  <Link
                    to="/profile/$identifier"
                    params={{ identifier }}
                    className="block group"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <p className="text-white font-bold text-[15px] leading-tight truncate group-hover:text-[color-mix(in_oklab,var(--event-accent)_75%,white)] transition-colors">
                      {creator.full_name ?? creator.username}
                    </p>
                    <p className="text-zinc-400 text-[11px] truncate mt-0.5">@{creator.username}</p>
                  </Link>
                </div>
              </div>
              {!isSelf && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleFollowToggle();
                  }}
                  disabled={followBusy}
                  className={`mt-auto w-full h-10 px-3 rounded-xl flex items-center justify-center text-[11px] font-semibold transition-colors disabled:opacity-50 ${
                    isFollowing
                      ? 'bg-zinc-800 border border-white/10 text-white hover:bg-zinc-700'
                      : 'bg-white text-black hover:bg-zinc-200'
                  }`}
                >
                  {followBusy ? 'Processing...' : isFollowing ? 'Following' : 'Follow'}
                </button>
              )}
            </div>
          </CoverImage>
        </div>

        {/* Right: stats, bio, associated creators */}
        <div className="flex-1 min-w-0 p-4 sm:p-5">
          <p className="text-white text-[11px] font-bold uppercase tracking-widest flex items-center justify-between gap-2 mb-3 sm:mb-4">
            <span className="flex items-center gap-2 min-w-0">
              <span className="w-1 h-3 rounded-full bg-(--event-accent) inline-block shrink-0" />
              <span>Hosted by</span>
            </span>
            <span className="w-8 h-8 rounded-full bg-zinc-900 border border-white/10 flex items-center justify-center text-white/90 shrink-0">
              <ExpandIcon />
            </span>
          </p>

          {typeof eventsCount === 'number' && eventsCount > 0 && (
            <div>
              <p className="text-white text-[30px] sm:text-[34px] font-bold leading-none">
                {eventsCount}
              </p>
              <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-medium mt-1.5">
                Events Hosted
              </p>
            </div>
          )}

          {bio && (
            <p className="text-zinc-400 text-[12px] leading-relaxed mt-3 line-clamp-4 sm:line-clamp-5">
              {bio}
            </p>
          )}

          {visibleCreators.length > 0 && (
            <div className="mt-4">
              <p className="text-zinc-500 text-[10px] uppercase tracking-wider font-medium mb-2">
                Associated Creators
              </p>
              <div className="flex flex-wrap gap-x-3 gap-y-2">
                {visibleCreators.map((c) => {
                  const displayName = c.full_name?.split(' ')[0] ?? c.username;

                  return (
                    <div key={c.id} className="flex items-center gap-1.5 min-w-0">
                      <Avatar
                        src={c.profile_image ?? undefined}
                        alt={c.username}
                        name={displayName || c.username}
                        size="xs"
                      />
                      <Link
                        to="/profile/$identifier"
                        params={{
                          identifier: getProfileIdentifier({ id: c.id, username: c.username }),
                        }}
                        className="text-zinc-300 text-[12px] font-medium truncate max-w-16 hover:text-white hover:underline transition-colors"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {displayName}
                      </Link>
                      {c.role && (
                        <span className="px-1.5 py-0.5 rounded-full bg-blue-500/15 border border-blue-500/25 text-blue-400 text-[10px] font-medium shrink-0">
                          {formatCreatorRole(c.role)}
                        </span>
                      )}
                    </div>
                  );
                })}
                {overflowCount > 0 && (
                  <div className="w-7 h-7 rounded-full bg-zinc-800 border border-white/10 flex items-center justify-center text-zinc-400 text-[11px] font-medium shrink-0">
                    +{overflowCount}
                  </div>
                )}
              </div>
            </div>
          )}

          <Link
            to="/profile/$identifier"
            params={{ identifier }}
            className="inline-flex items-center gap-1 text-zinc-500 text-[12px] font-medium hover:text-[color-mix(in_oklab,var(--event-accent)_75%,white)] transition-colors mt-3"
            onClick={(event) => event.stopPropagation()}
          >
            View Profile
            <FiArrowRight size={11} />
          </Link>
        </div>
      </div>

      {isHostModalOpen && (
        <HostDetailsModal
          creator={creator}
          identifier={identifier}
          isSelf={isSelf}
          isFollowing={isFollowing}
          followBusy={followBusy}
          bio={bio}
          eventsCount={eventsCount}
          associatedCreators={associatedCreators}
          onFollowToggle={handleFollowToggle}
          onClose={() => setIsHostModalOpen(false)}
        />
      )}

      {showAuthGate && (
        <AuthGateModal
          icon={
            <Avatar
              src={creator.prof_pics ?? creator.profile_image}
              alt={creator.username}
              name={creator.full_name || creator.username}
              size="xl"
            />
          }
          title={`Follow ${creator.full_name ?? creator.username}`}
          description="Create a free account to follow this organizer for updates on future shows and ticket releases."
          proofText="Join thousands of fans already following their favorite organizers on Musicade."
          ctaLabel="Create Account & Follow"
          onClose={() => setShowAuthGate(false)}
        />
      )}
    </div>
  );
}
